import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { z } from 'zod';

import { fg } from '@atlaskit/platform-feature-flags';
import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';
import {
	parseSurveyApiError,
	SurveyApiError,
} from '@atlassian/conversation-assistant-message-actions-api/survey-api-error';
import { useStagingAreaState } from '@atlassian/conversation-assistant-store/controllers/staging-area';
import type { JSMCreateSurveyData } from '@atlassian/rovo-content-bridge-api-commands/commands/jsm-create-survey';

import {
	MESSAGE_ACTION_INVOCATION_STATUSES,
	MESSAGE_ACTION_RESOLUTIONS,
} from '../../../common/constants';

import type { Props } from './JsmCreateSurveyCard';

const JsmActionDataSchema: z.ZodType<Omit<JSMCreateSurveyData, 'portalId'>> = z.object({
	selectedFormQuestions: z.object({
		title: z.string(),
		description: z.string(),
		questions: z.array(
			z.object({
				text: z.string(),
				type: z.string(),
				isRequired: z.boolean(),
				choices: z.array(z.string()).optional(),
			}),
		),
	}),
	projectId: z.string(),
});

const SurveySchema = z.object({
	id: z.string(),
});

const createSurvey = async (
	cloudId: string,
	actionData: Omit<JSMCreateSurveyData, 'portalId'>,
): Promise<{ id: string }> => {
	const res = await fetch(`/gateway/api/jsm/surveys/cloudid/${cloudId}/ai/create/survey`, {
		method: 'POST',
		headers: {
			'Content-Type': 'application/json',
		},
		body: JSON.stringify(actionData),
	});

	if (!res.ok) {
		const apiError = await parseSurveyApiError(res);
		throw apiError ?? new Error(`Failed to create survey with AI: ${res.status}`);
	}

	const json = await res.json();

	const survey = SurveySchema.safeParse(json);

	if (!survey.success) {
		throw new Error(`Failed to create survey with AI: ${survey.error.message}`);
	}

	return survey.data;
};

/**
 * Make the open survey URL for the JSM Create Survey preview card experience.
 *
 * After a fresh creation, use the specific survey edit URL; otherwise fall back to the
 * generic surveys dashboard so the Open button remains valid across page reloads.
 */
const makeOpenSurveyUrl = (
	projectKey: string,
	surveyId: string,
	conversationId: string,
): string => {
	const params = new URLSearchParams();

	params.append('conversationId', conversationId);

	let basePath = `/jira/servicedesk/projects/${projectKey}/surveys`;

	if (surveyId) {
		basePath += `/${surveyId}/edit`;
	}

	return `${basePath}?${params.toString()}`;
};

/**
 * Custom hook for managing the state and actions of the JSM Create Survey preview card experience.
 *
 * Provides state and callbacks to toggle the preview card open or closed, and exposes
 * flags for footer visibility, preview state, creation state, and action handlers.
 */
export const useJsmCreateSurvey = ({
	actions,
	isEnabled,
	isSelected,
	stagingAreaEnabled,
	cloudId,
	resolveAction,
	conversationId,
	projectKey,
}: Props & {
	projectKey: string;
}): {
	isOpen: boolean;
	handleToggle: () => void;
	isInSmartCreateModal: boolean;
	hasCreated: boolean;
	hasDismissed: boolean;
	isCreating: boolean;
	errorKey: string | undefined;
	onCreateSurvey: () => Promise<void>;
	onDismissSurvey: () => void;
	areButtonsDisabled: boolean;
	actionData: Omit<JSMCreateSurveyData, 'portalId'>;
	openSurveyUrl: string;
} => {
	const [firstAction] = actions;
	const { key: actionKey = undefined, data: actionData = {} } = firstAction ?? {};
	const { sendAnalyticsEvent, trackAIMAUEvent } = useAnalytics();
	const jsmCreateSurveyData = JsmActionDataSchema.parse(actionData);

	// When the survey is created from outside this card (e.g. by the staging-area
	// JsmSurveyFinalizeButton), the created survey's id is persisted on the action's
	// data. Its presence indicates the survey has already been created.
	const persistedSurveyId = (actionData as { surveyId?: string }).surveyId;

	const hasCreated =
		firstAction?.status === MESSAGE_ACTION_INVOCATION_STATUSES.finished ||
		Boolean(persistedSurveyId);

	const [isOpen, setIsOpen] = useState(false);
	const [isCreating, setIsCreating] = useState(false);
	const [errorKey, setErrorKey] = useState<string | undefined>(undefined);
	const [surveyId, setSurveyId] = useState<string | undefined>(undefined);
	const [hasDismissed, setHasDismissed] = useState(false);

	const { messageIdSelectedForPreview } = useStagingAreaState();

	const handleToggle = (): void => setIsOpen((prev) => !prev);

	const isInSmartCreateModal = Boolean(messageIdSelectedForPreview || stagingAreaEnabled);

	const openSurveyUrl = makeOpenSurveyUrl(projectKey, surveyId ?? '', conversationId ?? '');

	const lastInvocationId = useMemo(() => actions[actions.length - 1]?.invocationId, [actions]);
	const areButtonsDisabled = isSelected || !isEnabled || hasCreated || isCreating || hasDismissed;

	const hasFiredInitiated = useRef(false);
	useEffect(() => {
		if (
			!fg('surveys-aifc-dismiss-button') ||
			hasFiredInitiated.current ||
			!actionKey ||
			hasCreated
		) {
			return;
		}
		hasFiredInitiated.current = true;

		trackAIMAUEvent({
			type: 'initiated',
			attributes: {
				singleInstrumentationID: conversationId,
				aiFeatureName: 'jsmSurvey',
				createdContentProduct: 'jira',
				createdContentType: 'jsmSurvey',
				createdContentMetadata: { projectId: jsmCreateSurveyData.projectId },
			},
		});
	}, [actionKey, conversationId, hasCreated, jsmCreateSurveyData.projectId, trackAIMAUEvent]);

	const onDismissSurvey = useCallback((): void => {
		if (!actionKey || !lastInvocationId) {
			return;
		}

		sendAnalyticsEvent({
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: 'jsmSurveyPreviewCard.dismiss',
			attributes: {
				projectId: jsmCreateSurveyData.projectId,
			},
		});

		trackAIMAUEvent({
			type: 'dismissed',
			attributes: {
				singleInstrumentationID: conversationId,
				aiFeatureName: 'jsmSurvey',
				createdContentProduct: 'jira',
				createdContentType: 'jsmSurvey',
				createdContentMetadata: { projectId: jsmCreateSurveyData.projectId },
			},
		});

		resolveAction?.(actionKey, MESSAGE_ACTION_RESOLUTIONS.cancel, lastInvocationId);
		setHasDismissed(true);
	}, [
		actionKey,
		conversationId,
		jsmCreateSurveyData.projectId,
		lastInvocationId,
		resolveAction,
		sendAnalyticsEvent,
		trackAIMAUEvent,
	]);

	const onCreateSurvey = useCallback(async (): Promise<void> => {
		if (!actionKey || isCreating) {
			return;
		}

		// Fire analytics for create button click
		sendAnalyticsEvent({
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: 'jsmSurveyPreviewCard.create',
			attributes: {
				projectId: jsmCreateSurveyData.projectId,
			},
		});

		trackAIMAUEvent({
			type: 'actioned',
			actionResult: 'contentFinalized',
			attributes: {
				singleInstrumentationID: conversationId,
				aiFeatureName: 'jsmSurvey',
				createdContentProduct: 'jira',
				createdContentType: 'jsmSurvey',
				createdContentMetadata: { projectId: jsmCreateSurveyData.projectId },
			},
		});

		setIsCreating(true);
		setErrorKey(undefined);

		try {
			const { id } = await createSurvey(cloudId, jsmCreateSurveyData);
			setSurveyId(id);

			// Fire analytics for successful survey creation
			sendAnalyticsEvent({
				eventType: 'operational',
				action: 'succeeded',
				actionSubject: 'jsmSurveyCreation',
				actionSubjectId: 'jsmSurveyPreviewCard.create',
				attributes: {
					projectId: jsmCreateSurveyData.projectId,
					surveyId: id,
				},
			});

			resolveAction?.(actionKey, MESSAGE_ACTION_RESOLUTIONS.actioned, lastInvocationId, {
				surveyId: id,
			});

			trackAIMAUEvent({
				type: 'adopted',
				actionKey: actionKey ?? '',
				rovoModifiedObjects: [{ objectType: 'jsm-survey', objectId: id }],
				attributes: {
					singleInstrumentationID: conversationId,
					aiFeatureName: 'jsmSurvey',
					createdContentProduct: 'jira',
					createdContentType: 'jsmSurvey',
					createdContentMetadata: { projectId: jsmCreateSurveyData.projectId, surveyId: id },
				},
			});
		} catch (error) {
			// Fire analytics for failed survey creation
			sendAnalyticsEvent({
				eventType: 'operational',
				action: 'failed',
				actionSubject: 'jsmSurveyCreation',
				actionSubjectId: 'jsmSurveyPreviewCard.create',
				attributes: {
					projectId: jsmCreateSurveyData.projectId,
					errorMessage: error instanceof Error ? error.message : 'Unknown error',
				},
			});
			setErrorKey(error instanceof SurveyApiError ? error.errorKey : undefined);
		} finally {
			setIsCreating(false);
		}
	}, [
		resolveAction,
		actionKey,
		cloudId,
		isCreating,
		jsmCreateSurveyData,
		lastInvocationId,
		sendAnalyticsEvent,
		trackAIMAUEvent,
		conversationId,
	]);

	return {
		isOpen,
		handleToggle,
		isInSmartCreateModal,
		hasCreated,
		hasDismissed,
		isCreating,
		errorKey,
		onCreateSurvey,
		onDismissSurvey,
		areButtonsDisabled,
		actionData: jsmCreateSurveyData,
		openSurveyUrl,
	};
};
